import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, apiWrite } from '../lib/api'
import { PageHead, Skel } from '../components/ui'
import { thaiDateTime } from '../lib/format'

interface CacheScopeRow {
  scope: string
  label: string
  pages: string
  version: number
  updated_at: string
  ttl_seconds: number
}

const QUERY_KEY = ['cache-scopes']

export function Cache() {
  const qc = useQueryClient()
  const q = useQuery({ queryKey: QUERY_KEY, queryFn: () => api<CacheScopeRow[]>('/v1/cache/scopes') })
  const [bumping, setBumping] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  const bump = useMutation({
    mutationFn: (scope: string) => apiWrite<CacheScopeRow>(`/v1/cache/scopes/${scope}/bump`, 'POST'),
    onMutate: (scope) => {
      setBumping(scope)
      setError(null)
      setNotice(null)
    },
    onSuccess: (row) => {
      // Replace the row in place before the refetch lands, otherwise the old version flashes
      // back for a frame and the screen looks like the bump did nothing.
      qc.setQueryData<CacheScopeRow[]>(QUERY_KEY, (old) =>
        old ? old.map((x) => (x.scope === row.scope ? row : x)) : [row])
      setNotice(`เพิ่มเวอร์ชันของ “${row.label}” เป็น v${row.version} แล้ว — คำขอถัดไปจะสร้างแคชใหม่`)
      void qc.invalidateQueries({ queryKey: QUERY_KEY })
    },
    onError: (e) => {
      setError(e instanceof Error ? e.message : 'เพิ่มเวอร์ชันแคชไม่สำเร็จ')
    },
    onSettled: () => setBumping(null),
  })

  const rows = q.data ?? []
  const ttl = rows[0]?.ttl_seconds ?? 15
  const busy = bump.isPending

  return (
    <>
      <PageHead
        title="แคชคำตอบ HTTP"
        sub="เวอร์ชันต่อขอบเขตข้อมูล — เมื่อข้อมูลเปลี่ยน เวอร์ชันจะกระโดดและ ETag เก่าจะใช้ไม่ได้ทันที"
        actions={
          <span className="chip">
            {rows.length} ขอบเขต · TTL {ttl} วิ
          </span>
        }
      />

      {notice ? (
        <div className="card card-pad" style={{ marginBottom: 14, borderColor: 'var(--color-emerald)' }}>
          <div className="row" style={{ gap: 10, alignItems: 'center' }}>
            <span className="sev sev-low"><i className="sev-dot" />สำเร็จ</span>
            <span style={{ fontSize: 12.5 }}>{notice}</span>
          </div>
        </div>
      ) : null}

      {q.isLoading ? (
        <div className="stack"><Skel h={56} /><Skel h={56} /><Skel h={56} /></div>
      ) : q.isError ? (
        <div className="card card-pad">
          <div className="form-error">โหลดทะเบียนแคชไม่สำเร็จ — ตรวจสอบว่าเส้นทาง /v1/cache/scopes ถูก mount แล้ว</div>
        </div>
      ) : (
        <div className="table-wrap">
          <table className="data">
            <thead>
              <tr>
                <th>ขอบเขต</th>
                <th>หน้าเว็บที่ได้รับผลกระทบ</th>
                <th style={{ width: 110 }}>เวอร์ชัน</th>
                <th style={{ width: 170 }}>กระโดดล่าสุด</th>
                <th style={{ width: 130 }} />
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => {
                const pending = busy && bumping === row.scope
                return (
                  <tr key={row.scope} style={{ cursor: 'default' }}>
                    <td>
                      <div style={{ color: 'var(--color-ivory)' }}>{row.label}</div>
                      <div className="mono dim" style={{ fontSize: 11, marginTop: 3 }}>{row.scope}</div>
                    </td>
                    <td className="mono dim" style={{ fontSize: 11.5 }}>{row.pages}</td>
                    <td>
                      <span className="chip tnum" style={{ fontWeight: 600 }}>v{row.version}</span>
                    </td>
                    <td className="dim" style={{ fontSize: 11.5 }}>
                      {row.version === 0
                        ? 'ยังไม่เคยกระโดด'
                        : row.updated_at ? thaiDateTime(row.updated_at) : '—'}
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      <button
                        className="btn btn-sm btn-ghost"
                        type="button"
                        disabled={busy}
                        onClick={() => bump.mutate(row.scope)}
                        title={`เพิ่มเวอร์ชันของ ${row.scope} เพื่อให้ ETag ทั้งหมดในขอบเขตนี้เปลี่ยน`}
                      >
                        {pending ? 'กำลังกระโดด…' : 'กระโดดเวอร์ชัน'}
                      </button>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}

      {error ? <div className="form-error" style={{ marginTop: 14 }}>{error}</div> : null}

      <div className="card card-pad" style={{ marginTop: 22 }}>
        <div className="card-label">แคชนี้ทำงานอย่างไร</div>
        <ul className="dim" style={{ fontSize: 12.5, lineHeight: 1.9, margin: '12px 0 0', paddingLeft: 18 }}>
          <li>ทุกคำตอบถูกผูกกับคู่ (องค์กร, ขอบเขต) และหมายเลขเวอร์ชันที่เก็บในฐานข้อมูล — ไม่ใช่แค่เวลา</li>
          <li>เบราว์เซอร์เก็บคำตอบได้ {ttl} วินาที และ revalidate เบื้องหลังได้อีก 30 วินาทีด้วย <span className="mono">If-None-Match</span></li>
          <li>ETag เกิดจากเวอร์ชัน + hash ของเนื้อหา ดังนั้น bump จะเปลี่ยน ETag ของทุกคำขอในขอบเขตนั้น แม้เนื้อหาจะเหมือนเดิม</li>
          <li>การ bump ไม่ได้ล้างหน่วยความจำของ replica อื่นโดยตรง — มันเพิ่มเวอร์ชันในฐานข้อมูล แล้ว replica ทุกตัวจะเห็นว่า entry เก่าใช้ไม่ได้ในคำขอถัดไป</li>
          <li>ระบบตอบ <span className="mono">304 Not Modified</span> เมื่อ ETag ตรงกัน โดยไม่สร้างเนื้อหาซ้ำ และไม่ส่ง body กลับ</li>
          <li>หน้าที่ไม่ปลอดภัยต่อการแคช (สตรีม, ข้อมูลเฉพาะผู้ใช้, <span className="mono">no-store</span>) จะไม่ถูกห่อด้วย middleware นี้</li>
        </ul>
      </div>

      <div className="card card-pad" style={{ marginTop: 14 }}>
        <div className="card-label">ขอบเขตที่ลงทะเบียน</div>
        <p className="dim" style={{ fontSize: 12.5, lineHeight: 1.8, margin: '10px 0 0' }}>
          รายการนี้คือ allow-list ของทั้งหน้าจอและ middleware — เพิ่มขอบเขตใหม่ได้โดยเพิ่มหนึ่งบรรทัดใน
          <span className="mono"> cacheScopeCatalog </span>
          แล้วติด <span className="mono">s.CacheControl(&quot;ชื่อขอบเขต&quot;)</span> ที่ route ที่ต้องการ
          โดยไม่ต้องแก้ฐานข้อมูลหรือ deploy migration ใหม่
        </p>
      </div>
    </>
  )
}
